import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AppState } from '../../../core/storage/repository';
import { speechProvider } from '../../../core/speech/speechProvider';
import { MASTERY_BADGES, MasteryBadge, BadgeCategory } from '../../../core/badges/masteryBadges';
import confetti from 'canvas-confetti';

interface MasteryBadgesSectionProps {
  state: AppState;
  isOpen?: boolean;
  onToggle?: () => void;
  onNavigate: (view: 'alphabet' | 'writing' | 'syllables' | 'wordbuilder' | 'camera' | 'mywords' | 'games' | 'garden' | 'parent') => void;
}

const AnimatedBadgeIcon: React.FC<{ size?: 'sm' | 'md' }> = ({ size = 'md' }) => (
  <motion.svg
    viewBox="0 0 48 48"
    className={size === 'sm' ? 'w-6 h-6' : 'w-10 h-10 md:w-12 md:h-12'}
    role="img"
    aria-label="Lencana prestasi"
  >
    <path d="M15 5 H22 L24 19 L17 15 Z M33 5 H26 L24 19 L31 15 Z" fill="#4F7CF7" />
    <motion.circle
      cx="24"
      cy="29"
      r="11"
      fill="#FBBF24"
      stroke="#D97706"
      strokeWidth="2"
      animate={{ rotate: [0, -7, 7, 0], scale: [1, 1.05, 1] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
      style={{ originX: '24px', originY: '18px' }}
    />
    <path d="M24 22 L26 27 L31 27.5 L27 31 L28 36 L24 33.5 L20 36 L21 31 L17 27.5 L22 27 Z" fill="#FFF7E8" />
    <motion.circle cx="39" cy="10" r="2.5" fill="#FFF" animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity }} />
  </motion.svg>
);

export const MasteryBadgesSection: React.FC<MasteryBadgesSectionProps> = ({
  state,
  isOpen: controlledIsOpen,
  onToggle,
  onNavigate,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const handleToggle = () => {
    speechProvider.playChime('tap');
    if (!isOpen) {
      speechProvider.speak('Ayo lihat koleksi lencana prestasimu!', { rate: 0.9 });
    }
    if (onToggle) {
      onToggle();
    } else {
      setInternalIsOpen(!isOpen);
    }
  };

  const [activeCategory, setActiveCategory] = useState<BadgeCategory>('all');
  const [selectedBadge, setSelectedBadge] = useState<MasteryBadge | null>(null);

  // Compute unlocked count
  const unlockedBadges = MASTERY_BADGES.filter(b => b.isUnlocked(state));
  const totalBadges = MASTERY_BADGES.length;

  const filteredBadges = MASTERY_BADGES.filter(b => {
    if (activeCategory === 'all') return true;
    return b.category === activeCategory;
  });

  const handleSelectBadge = (badge: MasteryBadge) => {
    setSelectedBadge(badge);
    const unlocked = badge.isUnlocked(state);
    speechProvider.playChime(unlocked ? 'celebrate' : 'tap');

    if (unlocked) {
      confetti({
        particleCount: 40,
        spread: 60,
        origin: { y: 0.6 },
        colors: ['#F59E0B', '#10B981', '#3B82F6', '#EC4899'],
      });
      speechProvider.speak(badge.unlockedSpeech, { rate: 0.88 });
    } else {
      speechProvider.speak(badge.lockedSpeech, { rate: 0.88 });
    }
  };

  const handlePlayVoice = (badge: MasteryBadge) => {
    const unlocked = badge.isUnlocked(state);
    speechProvider.playChime('tap');
    speechProvider.speak(unlocked ? badge.unlockedSpeech : badge.lockedSpeech, { rate: 0.88 });
  };

  const handleCelebrateAgain = () => {
    speechProvider.playChime('celebrate');
    confetti({
      particleCount: 60,
      spread: 75,
      origin: { y: 0.55 },
      colors: ['#F59E0B', '#EF4444', '#10B981', '#3B82F6', '#8B5CF6'],
    });
  };

  // If collapsed: Show a compact, child-friendly trigger button/banner
  if (!isOpen) {
    return (
      <section
        id="mastery-badges-section"
        className="w-full bg-linear-to-r from-amber-50 via-yellow-50 to-orange-50 rounded-3xl p-4 md:p-5 border-3 border-amber-300 shadow-sm hover:shadow-md transition-all"
      >
        <div className="flex items-center justify-between gap-3">
          {/* Left: Icon & Info */}
          <div
            onClick={handleToggle}
            className="flex items-center gap-3 cursor-pointer flex-1"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-linear-to-br from-amber-400 to-yellow-500 text-white flex items-center justify-center shadow-xs shrink-0 ring-2 ring-amber-200">
              <AnimatedBadgeIcon />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base md:text-lg font-display font-black text-[#243047] leading-tight">
                  Lencana Prestasi
                </h3>
                <span className="bg-amber-200 text-amber-900 font-extrabold text-[11px] px-2 py-0.5 rounded-full">
                  {unlockedBadges.length}/{totalBadges}
                </span>
              </div>
              <p className="text-xs text-gray-600 font-medium mt-0.5">
                {unlockedBadges.length > 0
                  ? `Hore! Kamu sudah meraih ${unlockedBadges.length} lencana prestasi.`
                  : 'Selesaikan modul membaca untuk membuka lencanamu!'}
              </p>
            </div>
          </div>

          {/* Right: Expand Button */}
          <button
            onClick={handleToggle}
            className="px-4 py-2.5 rounded-2xl bg-linear-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 active:scale-95 text-amber-950 font-display font-black text-xs md:text-sm flex items-center gap-1.5 shadow-sm border border-amber-300 transition cursor-pointer shrink-0"
          >
            <span>Buka Lencana</span>
            <span className="text-sm">▼</span>
          </button>
        </div>
      </section>
    );
  }

  // If expanded: Show full Mastery Badges grid and controls
  return (
    <section
      id="mastery-badges-section"
      className="w-full bg-white rounded-3xl p-5 md:p-6 shadow-md border-3 border-amber-300 flex flex-col gap-4 animate-in fade-in duration-200"
    >
      {/* Header with Title, Counter, and Collapse Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b-2 border-amber-100 pb-3">
        <div className="flex items-center gap-2.5">
          <AnimatedBadgeIcon />
          <div>
            <h3 className="text-lg md:text-xl font-display font-black text-[#243047] leading-tight">
              Lencana Prestasi (Mastery Badges)
            </h3>
            <p className="text-xs text-gray-500 font-medium">
              Koleksi lencana setelah menguasai kelompok suku kata & modul kata!
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-center">
          {/* Counter Badge */}
          <div className="flex items-center gap-1.5 bg-linear-to-r from-amber-400 to-yellow-400 text-amber-950 font-display font-black text-xs md:text-sm px-3.5 py-1.5 rounded-2xl shadow-xs border border-amber-300 shrink-0">
            <span>⭐</span>
            <span>{unlockedBadges.length} / {totalBadges} Terbuka</span>
          </div>

          {/* Close/Collapse Button */}
          <button
            onClick={handleToggle}
            className="flex items-center gap-1 text-xs font-bold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-2xl transition active:scale-95 cursor-pointer"
            title="Tutup Lencana"
          >
            <span>Tutup</span>
            <span>▲</span>
          </button>
        </div>
      </div>

      {/* Filter Category Pills */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => {
            speechProvider.playChime('tap');
            setActiveCategory('all');
          }}
          className={`px-3.5 py-1.5 rounded-full text-xs font-black transition active:scale-95 cursor-pointer ${
            activeCategory === 'all'
              ? 'bg-amber-500 text-white shadow-xs'
              : 'bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200'
          }`}
        >
          Semua ({MASTERY_BADGES.length})
        </button>

        <button
          onClick={() => {
            speechProvider.playChime('tap');
            setActiveCategory('syllables');
          }}
          className={`px-3.5 py-1.5 rounded-full text-xs font-black transition active:scale-95 flex items-center gap-1 cursor-pointer ${
            activeCategory === 'syllables'
              ? 'bg-emerald-600 text-white shadow-xs'
              : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-200'
          }`}
        >
          <span>📖</span>
          <span>Suku Kata</span>
        </button>

        <button
          onClick={() => {
            speechProvider.playChime('tap');
            setActiveCategory('words');
          }}
          className={`px-3.5 py-1.5 rounded-full text-xs font-black transition active:scale-95 flex items-center gap-1 cursor-pointer ${
            activeCategory === 'words'
              ? 'bg-blue-600 text-white shadow-xs'
              : 'bg-blue-50 hover:bg-blue-100 text-blue-900 border border-blue-200'
          }`}
        >
          <span>🧩</span>
          <span>Susun Kata</span>
        </button>

        <button
          onClick={() => {
            speechProvider.playChime('tap');
            setActiveCategory('reading');
          }}
          className={`px-3.5 py-1.5 rounded-full text-xs font-black transition active:scale-95 flex items-center gap-1 cursor-pointer ${
            activeCategory === 'reading'
              ? 'bg-rose-500 text-white shadow-xs'
              : 'bg-rose-50 hover:bg-rose-100 text-rose-900 border border-rose-200'
          }`}
        >
          <span>🔍</span>
          <span>Dunia Nyata</span>
        </button>
      </div>

      {/* Badges Bento Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-3.5 pt-1">
        {filteredBadges.map((badge) => {
          const unlocked = badge.isUnlocked(state);
          const current = badge.currentCount(state);
          const percent = Math.min(100, Math.round((current / badge.targetCount) * 100));

          return (
            <motion.div
              key={badge.id}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleSelectBadge(badge)}
              className={`relative rounded-3xl p-3.5 md:p-4 flex flex-col items-center text-center cursor-pointer transition-all ${
                unlocked
                  ? 'bg-linear-to-b from-amber-50/80 via-white to-amber-50/40 border-3 border-amber-400 shadow-md ring-2 ring-amber-200/50'
                  : 'bg-gray-50/80 border-2 border-dashed border-gray-300 hover:border-amber-300 hover:bg-amber-50/30'
              }`}
            >
              {/* Status Ribbon Tag */}
              {unlocked ? (
                <div className="absolute -top-2.5 bg-linear-to-r from-amber-500 to-yellow-500 text-white font-display font-black text-[10px] px-2.5 py-0.5 rounded-full shadow-xs flex items-center gap-1">
                  <span>⭐</span>
                  <span>TERBUKA!</span>
                </div>
              ) : (
                <div className="absolute -top-2 bg-gray-200 text-gray-600 font-bold text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                  <span>🔒</span>
                  <span>{current}/{badge.targetCount}</span>
                </div>
              )}

              {/* Badge Icon Emblem */}
              <div
                className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center text-2xl md:text-3xl my-2 transition shadow-inner ${
                  unlocked
                    ? `bg-linear-to-br ${badge.colorTheme.badgeBg} text-white shadow-md ring-2 ring-amber-200`
                    : 'bg-gray-200/80 grayscale opacity-70'
                }`}
              >
                {badge.icon}
              </div>

              {/* Title & Subtitle */}
              <h4 className={`font-display font-black text-xs md:text-sm leading-tight line-clamp-1 ${
                unlocked ? 'text-[#243047]' : 'text-gray-600'
              }`}>
                {badge.title}
              </h4>
              <p className="text-[10px] text-gray-500 line-clamp-1 mt-0.5 font-medium">
                {badge.subtitle}
              </p>

              {/* Progress Indicator */}
              <div className="w-full mt-2.5">
                {unlocked ? (
                  <div className="w-full py-0.5 rounded-full bg-amber-100 text-amber-900 font-extrabold text-[10px] flex items-center justify-center gap-1">
                    <span>✨ Selesai!</span>
                  </div>
                ) : (
                  <div className="w-full">
                    <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-amber-400 h-full rounded-full transition-all duration-300"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="text-[10px] font-bold text-gray-500 mt-0.5 block">
                      {current} dari {badge.targetCount}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom Close Button for easy collapsing */}
      <div className="flex justify-center pt-2 border-t border-amber-100">
        <button
          onClick={handleToggle}
          className="px-5 py-2 rounded-2xl bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-950 font-bold text-xs flex items-center gap-1.5 cursor-pointer transition"
        >
          <span>▲ Sembunyikan Lencana Prestasi</span>
        </button>
      </div>

      {/* Detail Dialog Modal */}
      <AnimatePresence>
        {selectedBadge && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 350, damping: 22 }}
              className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border-4 border-amber-300 flex flex-col items-center text-center relative"
            >
              {/* Close Button */}
              <button
                onClick={() => {
                  speechProvider.playChime('tap');
                  setSelectedBadge(null);
                }}
                className="absolute top-3 right-3 w-9 h-9 bg-gray-100 hover:bg-gray-200 active:scale-90 text-gray-600 rounded-full font-bold flex items-center justify-center cursor-pointer transition"
              >
                ✕
              </button>

              {/* Big Emblem with Animation */}
              <motion.div
                animate={
                  selectedBadge.isUnlocked(state)
                    ? {
                        rotate: [0, -8, 8, 0],
                        scale: [1, 1.1, 1],
                      }
                    : { scale: 1 }
                }
                transition={{ repeat: Infinity, repeatDelay: 2.5, duration: 1 }}
                className={`w-24 h-24 rounded-3xl flex items-center justify-center text-5xl mb-3 shadow-lg ${
                  selectedBadge.isUnlocked(state)
                    ? `bg-linear-to-br ${selectedBadge.colorTheme.badgeBg} text-white ring-4 ring-amber-200`
                    : 'bg-gray-200/90 text-gray-400'
                }`}
              >
                {selectedBadge.icon}
              </motion.div>

              {/* Status Pill */}
              {selectedBadge.isUnlocked(state) ? (
                <span className="bg-amber-100 text-amber-900 border border-amber-300 px-3.5 py-1 rounded-full text-xs font-extrabold mb-1.5 flex items-center gap-1">
                  <span>⭐</span>
                  <span>LENCANA TELAH DIKUASAI!</span>
                </span>
              ) : (
                <span className="bg-gray-100 text-gray-700 px-3.5 py-1 rounded-full text-xs font-bold mb-1.5 flex items-center gap-1">
                  <span>🔒</span>
                  <span>SEDANG DIPELAJARI ({selectedBadge.currentCount(state)} / {selectedBadge.targetCount})</span>
                </span>
              )}

              {/* Title & Description */}
              <h3 className="text-xl font-display font-black text-[#243047]">
                {selectedBadge.title}
              </h3>
              <p className="text-xs font-medium text-gray-500 mt-1 mb-3">
                {selectedBadge.subtitle}
              </p>

              {/* Requirement Box */}
              <div className="w-full bg-amber-50/70 border border-amber-200 rounded-2xl p-3 text-xs text-amber-950 font-semibold mb-4 text-left">
                <span className="block text-[10px] uppercase tracking-wider text-amber-800 font-bold mb-1">
                  Syarat Meraih Lencana:
                </span>
                👉 {selectedBadge.requiredItemsDesc}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 w-full">
                {/* Voice Narration Button */}
                <button
                  onClick={() => handlePlayVoice(selectedBadge)}
                  className="w-full py-2.5 rounded-2xl bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-950 font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition shadow-2xs"
                >
                  <span>🔊</span>
                  <span>Dengarkan Rara Jelaskan</span>
                </button>

                {/* Navigation Button to Module */}
                <button
                  onClick={() => {
                    speechProvider.playChime('tap');
                    const target = selectedBadge.moduleTarget;
                    setSelectedBadge(null);
                    onNavigate(target);
                  }}
                  className="w-full py-3 rounded-2xl bg-linear-to-r from-[#4F7CF7] to-[#3B65DB] hover:from-[#3B65DB] hover:to-[#2F54BF] text-white font-display font-black text-sm flex items-center justify-center gap-2 active:scale-95 cursor-pointer shadow-md transition"
                >
                  <span>Ayo Buka Modul Ini</span>
                  <span>➡️</span>
                </button>

                {/* If unlocked, show Celebrate button */}
                {selectedBadge.isUnlocked(state) && (
                  <button
                    onClick={handleCelebrateAgain}
                    className="w-full py-2.5 rounded-2xl bg-linear-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-amber-950 font-black text-xs flex items-center justify-center gap-1.5 active:scale-95 cursor-pointer transition shadow-xs"
                  >
                    <span>✨</span>
                    <span>Rayakan dengan Bintang!</span>
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};
